import { useQuery } from '@tanstack/react-query';
import { Assignee } from 'infrastructure/enums';
import { apiUrlGenerator } from 'infrastructure/hooks/use-api';
import dataService from 'services/dataService';
import { useEffect } from 'react';
import { showResult } from 'infrastructure/hooks/utils';
import { mainlandTimezonesMapping } from 'infrastructure/data-sources/timezones';

import type {
  TDataFilter,
  TDataFilterWithPaginationMeta,
} from './use-rpm-overview-dashboard';

export interface RpmOverviewPatientStatusCount {
  critical: number;
  atRisk: number;
  normal: number;
}

interface TDataFilterWithoutPagination extends Omit<TDataFilter, 'page'> {
  page: undefined;
  items: undefined;
}

const url = 'reports/reports/interactive-time-dashboard-overview-count';

export const getRpmOverviewDashboardCountQueryKey = (
  filter: TDataFilterWithoutPagination,
) => [url, filter];

const queryFn = async (filter: TDataFilterWithoutPagination) => {
  const { data, error } =
    await dataService.getList<RpmOverviewPatientStatusCount>(
      apiUrlGenerator(url, filter),
    );

  if (error) {
    return Promise.reject(error);
  }

  return data;
};

export const useRpmOverviewDashboardCount = (
  filter: TDataFilterWithPaginationMeta,
) => {
  const { startTime, endTime, status, page: _, ...filterWithoutDates } = filter;
  const filterWithoutPagination: TDataFilterWithoutPagination = {
    ...filterWithoutDates,
    assignee: filter.assignee === Assignee.All ? undefined : filter.assignee,
    clinicGuid: filter.parentClinicGuid || filter.clinicGuid,
    childClinic: Boolean(filter.parentClinicGuid) || undefined,
    parentClinicGuid: undefined,
    year: endTime ? new Date(endTime).getFullYear() : undefined,
    month: endTime ? new Date(endTime).getMonth() + 1 : undefined,
    latestReadingStartTime: startTime,
    latestReadingEndTime: endTime,
    readingStatus: status,
    latestReading: filter.sorting,
    parentClinicOption:
      filter.parentClinicOption && filter.clinicGuid
        ? filter.parentClinicOption
        : undefined,
    timezone: filter.timezone
      ? mainlandTimezonesMapping[filter.timezone]
      : undefined,
    page: undefined,
    items: undefined,
  };

  const queryKey = getRpmOverviewDashboardCountQueryKey(
    filterWithoutPagination,
  );

  const { data, isLoading, isFetching, error } = useQuery<
    RpmOverviewPatientStatusCount | undefined,
    string | undefined
  >({
    queryKey,
    queryFn: async () => queryFn(filterWithoutPagination),
    keepPreviousData: true,
  });

  useEffect(() => {
    if (error) {
      showResult(error);
    }
  }, [error]);

  return {
    count: data,
    isLoading,
    isFetching,
  };
};
